import each from 'lodash/each';
import omit from 'lodash/omit';

import { unzipPayload } from './helpers/unzipPayload';
import { getOnlineParticipants } from './helpers/getOnlineParticipants';
import setRealtimeOfflineStatus from './helpers/setRealtimeOfflineStatus';
import getRealtimeOfflineStatus from './helpers/getRealtimeOfflineStatus';
import { DISCONNECTION_CODES } from './helpers/disconnectionCodes';

const EVENTS = {};

const getSubscriptionToken = async (context, endpoint, headers) => {
  const response = await fetch(endpoint, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      channels: [context.channel],
    }),
  });

  const jsonResponse = await response.json();

  return jsonResponse.channels[0].token;
};

const Sockets = {
  isConnected: false,
  connection: null,
  connectedAt: null,
  disconnectedAt: null,
  reconnectionAttempts: 0,
  subscriptionEndpoint: null,
  subscriptionHeaders: null,
  channels: {},

  on: (channel, events) => {
    if (!channel) {
      return;
    }
    // TODO:
    // check if channel is a string
    // check if events has keys

    EVENTS[channel] = EVENTS[channel] || {};
    each(events, (callback, eventName) => {
      EVENTS[channel][eventName] = [callback];
    });

    const existingSubscription = Sockets.connection.getSubscription(channel);

    const subscription = existingSubscription || Sockets.connection.newSubscription(channel, {
      getToken: (context) => getSubscriptionToken(
        context,
        Sockets.subscriptionEndpoint,
        Sockets.subscriptionHeaders,
      ),
      headers: Sockets.subscriptionHeaders,
    });

    subscription.on('publication', (context) => {
      const { event, resource } = context.data;
      if (EVENTS[channel][event] && EVENTS[channel][event].length) {
        context.data.message = unzipPayload(resource);
        each(EVENTS[channel][event], (callback) => {
          callback(context.data);
        });
      }
    });

    subscription.on('join', (context) => {
      if (EVENTS[channel].memberAdded && EVENTS[channel].memberAdded.length) {
        each(EVENTS[channel].memberAdded, (callback) => {
          callback(context.info.user.replace(/.+-/, ''));
        });
      }
    });

    subscription.on('leave', (context) => {
      if (EVENTS[channel].memberRemoved && EVENTS[channel].memberRemoved.length) {
        each(EVENTS[channel].memberRemoved, (callback) => {
          callback(context.info.user.replace(/.+-/, ''));
        });
      }
    });

    subscription.on('subscribed', (context) => {
      console.log('subscribed to', context.channel, 'at', new Date(), 'with events:', EVENTS[channel], 'context:', context);
    });

    subscription.on('unsubscribed', (context) => {
      console.log('unsubscribed from', context.channel, 'at', new Date(), 'context:', context);
    });

    Sockets.channels[channel] = subscription;

    subscription.subscribe();
  },

  off: (channel) => {
    if (!Sockets.channels[channel]) {
      return;
    }

    Sockets.channels[channel].unsubscribe();
    Sockets.channels[channel].removeAllListeners();
    Sockets.channels = omit(Sockets.channels, [channel]);
  },

  publish: async (channel, payload) => {
    if (!Sockets.channels[channel]) {
      return;
    }

    try {
      await Sockets.channels[channel].publish(payload);
    } catch (err) {
      console.log(err);
    }
  },

  presence: async (channel) => {
    if (!Sockets.channels[channel]) {
      return;
    }

    try {
      const data = await Sockets.channels[channel].presence();
      if (data.clients) {
        const isRealTimeOffline = getRealtimeOfflineStatus();
        if (!isRealTimeOffline) setRealtimeOfflineStatus(false);
        return getOnlineParticipants(data.clients);
      }
    } catch (err) {
      if (DISCONNECTION_CODES.includes(err.code)) {
        const isRealTimeOffline = getRealtimeOfflineStatus();
        if (!isRealTimeOffline) setRealtimeOfflineStatus(true);
      }
      console.log('error on presence request', channel, err, new Date());
    }
  },

  forceSubscribe: (channel) => {
    if (!Sockets.channels[channel]) {
      return;
    }
    Sockets.channels[channel].subscribe();
  },

  forceUnsubscribe: (channel) => {
    if (!Sockets.channels[channel]) {
      return;
    }
    Sockets.channels[channel].unsubscribe();
  },
};

export default Sockets;
